import { NextRequest, NextResponse } from 'next/server';

export const maxDuration = 120;

// ===============================
// CLOUDFLARE SETTINGS
// ===============================
const CLOUDFLARE_ACCOUNT_ID = '098a8fde368ceed2b868a8285f95b964';
const CLOUDFLARE_API_TOKEN = 'cfut_DrEtdYozoTZSmihP5AeXsXhwOD0RCpLTLfeZISuN20e56644';

const CLOUDFLARE_URL = `https://api.cloudflare.com/client/v4/accounts/${CLOUDFLARE_ACCOUNT_ID}/ai/run/@cf/black-forest-labs/flux-1-schnell`;

// ===============================
// TYPES
// ===============================
interface GenerateImageBody {
  productName: string;
  description: string;
  material: string;
  finish: string;
  color: string;
  height: string;
  width: string;
  depth: string;
  weight: string;
}

interface CloudflareResponse {
  result?: {
    image?: string;
  };
  success?: boolean;
  errors?: { message: string }[];
}

// ===============================
// BUILD PROMPT
// ===============================
function buildImagePrompt(body: GenerateImageBody): string {
  const {
    productName,
    description,
    material,
    finish,
    color,
    height,
    width,
    depth,
    weight,
  } = body;

  return `
Create a realistic premium furniture/product concept
for a handcrafted Indian furniture manufacturer.

The company specializes in:
- Metal casting
- Metal handicrafts
- Wood furniture
- Metal furniture
- Wood and metal furniture
- Handcrafted home decor

The product must look physically manufacturable.

Use realistic materials such as:
iron, brass, bronze, steel, teak, walnut
and natural wood.

Professional luxury product photography.
Premium export-quality furniture.
Realistic proportions.
Detailed materials.
Studio lighting.
Clean luxury background.
Photorealistic.
High-end furniture catalog photography.

PRODUCT DETAILS:

Product Name:
${productName}

Description:
${description || 'Premium handcrafted furniture product'}

Material:
${material || 'Natural wood and metal'}

Finish:
${finish || 'Premium handcrafted finish'}

Color:
${color || 'Natural wood / metallic tones'}

Height:
${height || 'Not specified'}

Width:
${width || 'Not specified'}

Depth:
${depth || 'Not specified'}

Weight:
${weight || 'Not specified'}

IMPORTANT:
- Make the product physically manufacturable.
- Use realistic materials.
- Follow the supplied dimensions and design.
- Show the complete product.
- No people.
- No text.
- No logos.
- No watermark.
- Professional commercial product photography.
`.trim();
}

// ===============================
// GENERATE SINGLE IMAGE VIA CLOUDFLARE
// ===============================
async function generateImage(prompt: string): Promise<string> {
  const response = await fetch(CLOUDFLARE_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${CLOUDFLARE_API_TOKEN}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      prompt,
      steps: 4,
    }),
    signal: AbortSignal.timeout(120000),
  });

  if (!response.ok) {
    const errorData = (await response.json()) as CloudflareResponse;
    const errorMessage =
      errorData?.errors?.[0]?.message ?? `Cloudflare API error ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = (await response.json()) as CloudflareResponse;

  const imageBase64 = data?.result?.image;

  if (!imageBase64) {
    throw new Error('No image returned by Cloudflare.');
  }

  // Cloudflare returns raw base64 → add data URI prefix
  return `data:image/jpeg;base64,${imageBase64}`;
}

// ===============================
// POST HANDLER
// ===============================
export async function POST(request: NextRequest) {
  try {
    // Parse request body
    const body: GenerateImageBody = await request.json();

    // Validate product name
    if (!body.productName?.trim()) {
      return NextResponse.json(
        { success: false, error: 'Product name is required.' },
        { status: 400 }
      );
    }

    // Build prompt
    const prompt = buildImagePrompt(body);

    // Generate 4 images in parallel
    const imageRequests = Array.from({ length: 4 }, () =>
      generateImage(prompt)
    );

    const results = await Promise.allSettled(imageRequests);

    const images: string[] = [];

    for (const result of results) {
      if (result.status === 'fulfilled') {
        images.push(result.value);
      } else {
        console.error('Cloudflare image generation failed:', result.reason);
      }
    }

    // All failed
    if (images.length === 0) {
      return NextResponse.json(
        {
          success: false,
          error: 'All image generation attempts failed. Please try again.',
        },
        { status: 500 }
      );
    }

    // Return successful images
    return NextResponse.json({
      success: true,
      images,
      generated: images.length,
      requested: 4,
    });

  } catch (error) {
    console.error('Image generation error:', error);

    const message =
      error instanceof Error
        ? error.message
        : 'Failed to generate images';

    return NextResponse.json(
      { success: false, error: message },
      { status: 500 }
    );
  }
}

// import { NextRequest, NextResponse } from 'next/server';
// import { getOpenAIClient, buildImagePrompt } from '@/lib/openai';

// export const maxDuration = 120;

// interface GenerateImageBody {
//   productName: string;
//   description: string;
//   material: string;
//   finish: string;
//   color: string;
//   height: string;
//   width: string;
//   depth: string;
//   weight: string;
// }

// export async function POST(request: NextRequest) {
//   try {
//     if (!process.env.OPENAI_API_KEY) {
//       // Return placeholder images if no API key
//       const placeholders = Array.from(
//         { length: 4 },
//         (_, i) =>
//           `https://placehold.co/800x800/1a1a2e/c9a84c?text=AI+Product+${i + 1}`
//       );
//       return NextResponse.json({ images: placeholders });
//     }

//     const body: GenerateImageBody = await request.json();
//     const {
//       productName,
//       description,
//       material,
//       finish,
//       color,
//       height,
//       width,
//       depth,
//       weight,
//     } = body;

//     if (!productName) {
//       return NextResponse.json(
//         { error: 'Product name is required' },
//         { status: 400 }
//       );
//     }

//     const prompt = buildImagePrompt({
//       productName,
//       description,
//       material,
//       finish,
//       color,
//       height,
//       width,
//       depth,
//       weight,
//     });

//     const client = getOpenAIClient();

//     // Generate 4 images
//     const imagePromises = Array.from({ length: 4 }, () =>
//       client.images.generate({
//         model: 'gpt-image-1',
//         prompt,
//         n: 1,
//         size: '1024x1024',
//         quality: 'standard',
//       })
//     );

//     const results = await Promise.allSettled(imagePromises);

//     const images: string[] = [];
//     for (const result of results) {
//       if (result.status === 'fulfilled') {
//         const imgData = result.value.data?.[0];
//         if (imgData?.url) {
//           images.push(imgData.url);
//         } else if (imgData?.b64_json) {
//           images.push(`data:image/png;base64,${imgData.b64_json}`);
//         }
//       }
//     }

//     if (images.length === 0) {
//       return NextResponse.json(
//         { error: 'Failed to generate images. Please try again.' },
//         { status: 500 }
//       );
//     }

//     return NextResponse.json({ images });
//   } catch (error) {
//     console.error('Image generation error:', error);
//     const message =
//       error instanceof Error ? error.message : 'Failed to generate images';
//     return NextResponse.json({ error: message }, { status: 500 });
//   }
// }
